<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">

      <div class="feature-products">
        <BaseFilter
          screenKey="payments-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>

      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Payments</h3>
          <div class="table-tools d-flex gap-2 flex-wrap">
            <button
              v-if="isAdmin && bulkApprovableIds.length > 0"
              class="btn btn-sm btn-success"
              :disabled="bulkProcessing"
              @click="onBulkApprove"
            >
              <i class="fa fa-check me-1"></i> Approve ({{ bulkApprovableIds.length }})
            </button>
            <button
              v-if="bulkCancellableIds.length > 0"
              class="btn btn-sm btn-danger"
              :disabled="bulkProcessing"
              @click="onBulkCancel"
            >
              <i class="fa fa-ban me-1"></i> Reject ({{ bulkCancellableIds.length }})
            </button>
            <button
              class="btn btn-sm btn-success"
              @click="isExportOpen = true"
            >
              <i class="fa fa-download me-1"></i> Export
            </button>
            <button v-if="isAdmin" class="btn btn-sm btn-primary" @click="openManualModal">
              <i class="fa fa-plus me-1"></i> New Payment
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="fetchData">
              <i class="fa fa-refresh me-1"></i> Refresh
            </button>
          </div>
        </div>

        <BaseTable
          storage-key="payments"
          ref="tableRef"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(user)="{ item }">
            {{ item.user?.email || '—' }}
          </template>
          <template #cell(order_id)="{ item }">
            {{ item.order?.code || item.order_id }}
          </template>
          <template #cell(service_id)="{ item }">
            {{ getServiceName(item.order?.service_id) }}
          </template>
          <template #cell(amount)="{ item }">
            <span class="text-primary fw-bold">
              {{ Number(item.amount).toLocaleString() }}
            </span>
          </template>
          <template #cell(status)="{ item }">
            <span class="badge" :class="getStatusBadgeClass(item.status?.name)">
              {{ item.status?.display_name || item.status?.name || '—' }}
            </span>
          </template>
          <template #cell(is_approve)="{ item }">
            <span :class="isApproved(item) ? 'badge bg-success' : 'badge bg-secondary'">
              {{ isApproved(item) ? 'Yes' : 'No' }}
            </span>
          </template>
          <template #cell(description)="{ item }">
             <span v-if="item.description" class="d-inline-block text-truncate" style="max-width: 50px;" :title="item.description">
              {{ item.description }}
            </span>
          </template>
          <template #cell(creator_id)="{ item }">
            {{ item.creator?.name || item.creator_id || '—' }}
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #cell(actions)="{ item }">
            <div class="d-flex gap-1 justify-content-center">
              <button
                v-if="isPendingDeposit(item)"
                class="btn btn-sm btn-outline-primary"
                :disabled="actionId === item.id"
                title="View QR"
                @click="handleShowQr(item)"
              >
                <i class="fa fa-qrcode"></i>
              </button>
              <button
                v-if="isAdmin && canApprove(item)"
                class="btn btn-sm btn-success"
                :disabled="approvingId === item.id"
                title="Approve"
                @click="handleApprove(item)"
              >
                <i class="fa fa-check"></i>
              </button>
              <button
                v-if="canCancel(item)"
                class="btn btn-sm btn-outline-danger"
                :disabled="actionId === item.id || approvingId === item.id"
                title="Cancel"
                @click="handleCancel(item)"
              >
                <i class="fa fa-ban"></i>
              </button>
            </div>
          </template>
        </BaseTable>
      </div>
    </div>

    <ManualPaymentModal :is-open="showManualModal" @close="showManualModal = false" @created="onManualCreated" />
    <ExportPaymentModal
      :isOpen="isExportOpen"
      :filterParams="allParams"
      :selectedIds="selectedIds"
      @close="isExportOpen = false"
    />

    <div v-if="qrPreview.open" class="modal fade show" tabindex="-1" style="display: block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg">
          <div class="modal-header border-0">
            <h5 class="modal-title fw-bold">Mã QR thanh toán</h5>
            <button type="button" class="btn-close" @click="closeQrPreview"></button>
          </div>
          <div class="modal-body text-center py-4">
            <img :src="qrPreview.image" alt="VietQR" class="img-fluid rounded-3 mb-3" style="max-height: 280px;" />
            <div class="small">
              <div>Số tiền: <strong>{{ Number(qrPreview.amount).toLocaleString('vi-VN') }} ₫</strong></div>
              <div>Nội dung: <code>{{ qrPreview.code }}</code></div>
            </div>
            <div class="alert alert-danger small mt-3 mb-0 text-start">
              <strong>Lưu ý:</strong> Tuyệt đối không sửa nội dung chuyển khoản.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import ManualPaymentModal from "@/components/payments/ManualPaymentModal.vue";
import ExportPaymentModal from "@/components/payments/ExportPaymentModal.vue";
import { usePayments } from "@/composables/usePayments";
import { useService } from "@/composables/useService";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { useUser } from "@/composables/useUser";
import { createToaster } from "@meforma/vue-toaster";
import {
  PAYMENT_TYPES,
  PAYMENT_TYPE_DEPOSIT,
  PAYMENT_TYPE_WITHDRAW,
  PAYMENT_TYPE_DISCOUNT,
  PAYMENT_TYPE_MAP,
} from "@/constants/paymentTypes";
import { useStatus } from "@/composables/useStatus";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const {
  fetchPaymentsApi,
  patchPaymentApi,
  bulkPatchPaymentsApi,
  getQrInfoApi,
} = usePayments();

const { currentUser, loadCurrentUser } = useCurrentUser();
const ADMIN_ROLES = ['administrator', 'admin', 'owner', 'accountant', 'staff', 'sale', 'operation'];
const isAdmin = computed(() => {
  const roles = (currentUser.value?.roles || []).map(r => (r?.name || '').toLowerCase());
  if (!roles.length) {
    const fallback = (localStorage.getItem('user_role') || '').toLowerCase();
    return ADMIN_ROLES.includes(fallback);
  }
  return roles.some(r => ADMIN_ROLES.includes(r));
});
const showManualModal = ref(false);
const isExportOpen = ref(false);
const approvingId = ref(null);
const actionId = ref(null);
const qrPreview = ref({ open: false, image: '', code: '', amount: 0 });
const paymentStatuses = ref([]);
const { getStatusesApi } = useStatus();

const STATUS_WAIT_PAY        = 'payment-wait-pay';
const STATUS_PENDING_APPROVE = 'payment-pending-approve';
const STATUS_PAID            = 'payment-paid';
const STATUS_REJECTED        = 'payment-rejected';
const STATUS_REFUND          = 'payment-refund';

const getStatusBadgeClass = (statusName) => {
  switch (statusName) {
    case STATUS_WAIT_PAY:
      return 'bg-warning text-dark';
    case STATUS_PENDING_APPROVE:
      return 'bg-info text-white';
    case STATUS_PAID:
    case 'approved':
      return 'bg-success';
    case STATUS_REJECTED:
    case 'cancelled':
      return 'bg-danger';
    case STATUS_REFUND:
      return 'bg-primary';
    default:
      return 'bg-secondary';
  }
};

const CANCELLABLE_STATUSES   = [STATUS_WAIT_PAY, STATUS_PENDING_APPROVE];

const isCancellableStatus = (item) => CANCELLABLE_STATUSES.includes(item?.status?.name);

const isApproved = (item) => Number(item?.is_approve) === 1 || item?.is_approve === true;

const canApprove = (item) => {
  if (!isCancellableStatus(item)) return false;
  const type = Number(item?.payment_type);
  return type === PAYMENT_TYPE_DEPOSIT || type === PAYMENT_TYPE_DISCOUNT || type === PAYMENT_TYPE_WITHDRAW;
};

const isPendingDeposit = (item) =>
  Number(item?.payment_type) === PAYMENT_TYPE_DEPOSIT
  && item?.status?.name === STATUS_WAIT_PAY
  && Number(item?.amount) > 0;

const openManualModal = () => { showManualModal.value = true; };
const onManualCreated = () => { showManualModal.value = false; fetchData(true); };

const canCancel = (item) => {
  if (!isCancellableStatus(item)) return false;
  if (isPendingDeposit(item)) return true;
  if (isAdmin.value) {
    const type = Number(item?.payment_type);
    return type === PAYMENT_TYPE_DEPOSIT || type === PAYMENT_TYPE_DISCOUNT || type === PAYMENT_TYPE_WITHDRAW;
  }
  return false;
};

const handleApprove = async (item) => {
  approvingId.value = item.id;
  try {
    const res = await patchPaymentApi(item.id, 'approved');
    if (res.success) {
      toaster.success('Payment approved');
      fetchData();
    } else {
      toaster.error(res.error || 'Approval failed');
    }
  } finally {
    approvingId.value = null;
  }
};

const handleCancel = async (item) => {
  const amount = Number(item.amount).toLocaleString('vi-VN');
  const msg = isPendingDeposit(item)
    ? `Cancel deposit ${amount} ₫ (${item.code})?`
    : `Cancel payment ${item.code}?`;
  if (!confirm(msg)) return;
  actionId.value = item.id;
  try {
    const res = await patchPaymentApi(item.id, 'cancelled');
    if (res.success) {
      toaster.success('Payment cancelled');
      fetchData();
    } else {
      toaster.error(res.error || 'Cancel failed');
    }
  } finally {
    actionId.value = null;
  }
};

const bulkProcessing = ref(false);
const selectedRows = ref([]);
const tableRef = ref(null);

const resetSelection = () => {
  selectedRows.value = [];
  tableRef.value?.clearSelection();
};

const bulkApprovableIds = computed(() =>
  selectedRows.value.filter(r => canApprove(r)).map(r => r.id)
);

const bulkCancellableIds = computed(() =>
  selectedRows.value.filter(r => canCancel(r)).map(r => r.id)
);

const selectedIds = computed(() => selectedRows.value.map(row => row.id));

const onBulkApprove = async () => {
  const ids = bulkApprovableIds.value;
  if (!ids.length) return;
  if (!confirm(`Approve ${ids.length} payment(s)?`)) return;
  bulkProcessing.value = true;
  try {
    const res = await bulkPatchPaymentsApi(ids, 'approved');
    if (res.success) {
      const { succeeded = [], failed = [] } = res.data?.data || res.data || {};
      if (succeeded.length) toaster.success(`${succeeded.length} payment(s) approved`);
      if (failed.length) toaster.error(`${failed.length} payment(s) failed`);
    } else {
      toaster.error(res.error || 'Bulk approve failed');
    }
    resetSelection();
    fetchData();
  } finally {
    bulkProcessing.value = false;
  }
};

const onBulkCancel = async () => {
  const ids = bulkCancellableIds.value;
  if (!ids.length) return;
  if (!confirm(`Cancel/reject ${ids.length} payment(s)?`)) return;
  bulkProcessing.value = true;
  try {
    const res = await bulkPatchPaymentsApi(ids, 'cancelled');
    if (res.success) {
      const { succeeded = [], failed = [] } = res.data?.data || res.data || {};
      if (succeeded.length) toaster.success(`${succeeded.length} payment(s) processed`);
      if (failed.length) toaster.error(`${failed.length} payment(s) failed`);
    } else {
      toaster.error(res.error || 'Bulk cancel failed');
    }
    resetSelection();
    fetchData();
  } finally {
    bulkProcessing.value = false;
  }
};

const handleShowQr = async (item) => {
  try {
    const res = await getQrInfoApi(item.id);
    if (!res.success) {
      toaster.error(res.error || 'Không lấy được thông tin QR');
      return;
    }

    const info = res.data?.data || res.data;
    const bank = info.bank_info;

    if (!bank) {
      toaster.error('Không tìm thấy thông tin ngân hàng thụ hưởng');
      return;
    }

    const axios = (await import('axios')).default;
    const vietqr = await axios.post('https://api.vietqr.io/v2/generate', {
      accountNo: bank.account_number,
      accountName: bank.account_name,
      acqId: bank.acq_id,
      addInfo: info.code,
      amount: info.amount,
      template: 'compact2',
    });
    if (vietqr.data?.code === '00') {
      qrPreview.value = {
        open: true,
        image: vietqr.data.data.qrDataURL,
        code: info.code,
        amount: info.amount,
      };
    } else {
      toaster.error(vietqr.data?.desc || 'Không tạo được QR');
    }
  } catch (err) {
    toaster.error('Lỗi khi tải QR');
  } finally {
    actionId.value = null;
  }
};

const closeQrPreview = () => { qrPreview.value.open = false; };
const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const total = ref(0);

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  searchJoin: 'AND',
  include: 'user,order,creator,status,bankAccount',
  searchFields: '',
  search: '',
});

// Cấu hình các ô nhập của Filter
const filterConfig = computed(() => {
  const filters = [
    {
      key: 'code',
      type: 'text',
      label: 'Code',
      operator: 'like',
      placeholder: 'Code...',
      isDefault: true
    },
    {
      key: 'order_code',
      type: 'text',
      label: 'Order Code',
      operator: 'like',
      placeholder: 'Order code...',
      isDefault: true
    }
  ];

  if (isAdmin.value) {
    filters.push({
      key: 'user_email',
      type: 'text',
      label: 'User Email',
      operator: 'like',
      placeholder: 'Email...',
      isDefault: true
    });
    filters.push({
      key: 'sale_id',
      type: 'select',
      label: 'Collaborator (Sale)',
      operator: '=',
      options: sales.value.map(s => ({
        label: s.name,
        value: s.id
      }))
    });
  }

  filters.push(
    {
      key: 'payment_type',
      type: 'select',
      label: 'Type',
      operator: '=',
      isDefault: true,
      options: Object.values(PAYMENT_TYPES).map(t => ({
        label: t.label,
        value: t.value
      }))
    },
    {
      key: 'status_id',
      type: 'select',
      label: 'Status',
      operator: '=',
      isDefault: true,
      options: paymentStatuses.value.map(s => ({
        label: s.display_name,
        value: s.id
      }))
    },
    {
      type: 'daterange',
      fromKey: 'created_date_from',
      toKey: 'created_date_to',
      searchField: 'date_payment',
      label: 'Created Date',
      isDefault: true
    }
  );

  return filters;
});

const tableCols = computed(() => {
  const cols = [
    { key: 'id', label: 'ID', width: '70px', sortable: true },
    { key: 'code', label: 'Code' },
    { key: 'user', label: 'User' },
    { key: 'order_id', label: 'Order' },
    { key: 'service_id', label: 'Service' },
    { key: 'amount', label: 'Amount', align: 'center' },
    { key: 'status', label: 'Status', align: 'center' },
    { key: 'payment_type_name', label: 'Type' },
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'bank_number', label: 'Bank Number' },
    { key: 'is_approve', label: 'Approved', align: 'center' },
    { key: 'creator_id', label: 'Created By' },
    { key: 'description', label: 'Description' },
    { key: 'created_at', label: 'Created At' },
    { key: 'actions', label: 'Actions', align: 'center' },
  ];

  if (isAdmin.value) {
    // Không cần thêm user_id nữa theo yêu cầu mới
  }

  return cols;
});

const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  loading.value = true;

  try {
    const res = await fetchPaymentsApi({ ...allParams });

    if (res.success && res.data) {
      tableData.value = res.data.data || [];

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';

  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  const idSet = new Set(ids);
  selectedRows.value = tableData.value.filter(r => idSet.has(r.id));
};

watch(() => [allParams.page, allParams.limit], () => fetchData());

const services = ref([]);
const { getServicesApi } = useService();
const sales = ref([]);
const { getUsersApi } = useUser();

const fetchDependencies = async () => {
  try {
    const [serviceRes, statusRes] = await Promise.all([
      getServicesApi({ limit: 1000 }),
      getStatusesApi({ limit: -1 })
    ]);

    if (serviceRes.success && serviceRes.data) {
      services.value = serviceRes.data.data || [];
    }

    if (statusRes.success && statusRes.data) {
      paymentStatuses.value = (statusRes.data.data || []).filter(s => s.scope === 'payment');
    }

    if (isAdmin.value) {
      const salesRes = await getUsersApi({ search: 'role_id:5', limit: 1000, orderBy: 'name', sortedBy: 'asc' });
      if (salesRes.success && salesRes.data) {
        sales.value = salesRes.data.data || [];
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const getServiceName = (id) => {
  if (!id) return '';
  const service = services.value.find(s => s.id === Number(id));
  return service ? service.name : id;
};

const onPaymentsChanged = () => fetchData();

onMounted(async () => {
  await loadCurrentUser();
  fetchDependencies();
  fetchData();
  window.addEventListener('payments:changed', onPaymentsChanged);
});

onBeforeUnmount(() => {
  window.removeEventListener('payments:changed', onPaymentsChanged);
});

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
</style>
